<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { onClickOutside, useElementSize, useParentElement } from '@vueuse/core';
import IconListItem from '../IconListItem.vue';
import { getFormattedFileSize } from '../../../utils/getFormattedFileSize';
import { useBreakpoints } from '../../../composables/breakpoint';
import AppIcon from '../../atoms/app/AppIcon.vue';
import AppLink from '../../atoms/app/AppLink.vue';
import CircularProgressIndicator from '../../atoms/progress/CircularProgressIndicator.vue';
import { track } from '../../../utils/tracking';
import { usePdfPreview } from '../../../composables/pdf-preview';

const props = defineProps({
    file: {
        type: Object,
        default: undefined,
    },
    uploadProgress: {
        type: Number,
        default: undefined,
    },
    isCurrentlyUploading: {
        type: Boolean,
        default: false,
    },
    disableAttachmentActivation: {
        type: Boolean,
        default: false,
    },
    isPreview: {
        type: Boolean,
        default: false,
    },
    boldTitle: {
        type: Boolean,
        default: false,
    },
});
const emit = defineEmits(['abort-upload', 'remove-file-from-queue', 'attachment-changed', 'remove-uploaded-cv']);

const { isMobile } = useBreakpoints();

// Cut off file name after certain px points to avoid ugly overflow behavior
// Browser-based ellipsis shows bad results depending on the file names
const attachmentTitleElement = ref(null);
const parent = useParentElement(attachmentTitleElement);
const { width } = useElementSize(parent);
const maxFileNameLengthInCharacters = computed(() => Math.max(10, Math.round(width.value / 10)));
const fileNameToDisplay = computed(() => {
    const parts = props.file.name?.toLowerCase().split('.');

    const fileExt = parts.pop();
    let fileNameWithoutExt = parts.join('.');

    if (fileNameWithoutExt.length > maxFileNameLengthInCharacters.value) {
        fileNameWithoutExt = `${fileNameWithoutExt.substring(0, maxFileNameLengthInCharacters.value - 3)}...`;
    }
    return `${fileNameWithoutExt}.${fileExt}`;
});

const fileName = computed(() => props.file.name?.split('.').slice(0, -1).join('.')
    .replaceAll(' ', '-')
    .replaceAll('.', '')
    .toLowerCase());
const fileExtension = computed(() => `.${props.file.name?.split('.').pop()}`);
const fileSize = computed(() => getFormattedFileSize(props.file.size));
const showUploadProgress = computed(() => props.uploadProgress !== undefined);
const trackingAttachmentType = computed(() => 'Uploaded cv');

// Use previewLink property for uploaded files
const previewLink = computed(() => props.file.previewLink);

const imageFileExtensions = ['.jpg', '.png', '.jpeg', '.gif'];
const textFileExtensions = ['.doc', '.docx', '.odt', '.rtf'];
const pdfFileExtensions = ['.pdf'];

const FILE_TYPES = {
    IMAGE: 0,
    TEXT: 1,
    PDF: 2,
    UNKNOWN: 3,
};

const fileType = computed(() => {
    if (imageFileExtensions.includes(fileExtension.value)) {
        return FILE_TYPES.IMAGE;
    } if (textFileExtensions.includes(fileExtension.value)) {
        return FILE_TYPES.TEXT;
    } if (pdfFileExtensions.includes(fileExtension.value)) {
        return FILE_TYPES.PDF;
    }
    return FILE_TYPES.UNKNOWN;
});

function removeUploadedCv() {
    emit('remove-uploaded-cv');
}

function abortUpload() {
    if (props.isCurrentlyUploading) {
        emit('abort-upload');
    } else {
        emit('remove-file-from-queue', props.file.name);
    }
    track({
        element: 'AP_F: Unfinished upload aborted',
    });
}

function trackPreview() {
    track({
        element: `AP_F: ${trackingAttachmentType.value}`,
        elementDetail: 'Previewed',
    });
}

/* Mobile options dropdown */
const optionsDropdown = ref(null);
const dropdownDots = ref(null);
const dropdownActive = ref(false);
const toggleDropdown = () => {
    dropdownActive.value = !dropdownActive.value;
    track({
        element: 'AP_F: Action menu click without action',
    });
};
onClickOutside(optionsDropdown, () => { dropdownActive.value = false; }, { ignore: [dropdownDots] });

/* PDF preview generation */
const canvas = ref(null);
const { generatePdfPreview, pdfPreviewAvailable, currentlyGeneratingPreview } = usePdfPreview();

onMounted(() => watch(() => props.file, (file) => {
    if (!file.previewLink || !(fileType.value === FILE_TYPES.PDF)) return;
    // Make sure there are no preview generations currently running
    if (currentlyGeneratingPreview.value) return;

    generatePdfPreview({
        fileUrl: file.previewLink,
        canvas,
        fileOrigin: trackingAttachmentType.value,
    });
}, { immediate: true }));

/* Image preview - show fallback if img can't be loaded */
const showImageFallback = ref(false);
const onImageLoadError = () => { showImageFallback.value = true; };
</script>

<template>
    <IconListItem
        underline-title
        align-actions-top
    >
        <template #icon>
            <template v-if="showUploadProgress">
                <CircularProgressIndicator
                    class="c-fileListEntry__progressIndicator"
                    data-qa="upload progress circle"
                    :progress="uploadProgress"
                />
            </template>
            <template v-else>
                <AppLink
                    neutral
                    class="h-full w-full flex items-center justify-center"
                    tag="a"
                    :href="previewLink"
                    target="_blank"
                    rel="noopener noreferrer"
                    tabindex="-1"
                    @click="trackPreview"
                >
                    <template v-if="fileType === FILE_TYPES.IMAGE">
                        <img
                            v-if="file.previewLink && !showImageFallback"
                            :src="previewLink"
                            class="object-cover w-full h-full"
                            alt=""
                            @error="onImageLoadError"
                        >
                        <AppIcon
                            v-else
                            name="picture-stack"
                            size="xl"
                        />
                    </template>
                    <template v-else-if="fileType === FILE_TYPES.TEXT">
                        <AppIcon
                            name="file-text"
                            size="xl"
                        />
                    </template>
                    <template v-else-if="fileType === FILE_TYPES.PDF">
                        <canvas
                            v-show="pdfPreviewAvailable"
                            ref="canvas"
                            class="c-fileListEntry__pdfPreview"
                        />
                        <AppIcon
                            v-if="!pdfPreviewAvailable"
                            name="file-pdf"
                            size="xl"
                        />
                    </template>
                    <template v-else-if="fileType === FILE_TYPES.UNKNOWN">
                        <AppIcon
                            name="file"
                            size="xl"
                        />
                    </template>
                </AppLink>
            </template>
        </template>
        <template #title>
            <div
                ref="attachmentTitleElement"
                :class="boldTitle && 'font-bold'"
            >
                {{ fileNameToDisplay }}
            </div>
        </template>
        <template
            v-if="!showUploadProgress"
            #subtitle
        >
            {{ fileSize }}
        </template>
        <template
            #actions
        >
            <template v-if="isPreview">
                <AppLink
                    neutral
                    class="c-fileListEntry__action"
                    :aria-label="`${fileName} in neuem Tab betrachten`"
                    :href="previewLink"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-qa="preview link"
                    @click="trackPreview"
                >
                    <AppIcon
                        name="preview"
                        size="xl"
                    />
                </AppLink>
            </template>
            <template v-else-if="isMobile">
                <template v-if="showUploadProgress">
                    <AppLink
                        tag="button"
                        neutral
                        class="c-fileListEntry__action"
                        :aria-label="`Upload von ${fileName} abbrechen`"
                        type="button"
                        @click="abortUpload"
                    >
                        <AppIcon
                            name="cross"
                            size="xl"
                        />
                    </AppLink>
                </template>
                <template v-else>
                    <AppLink
                        ref="dropdownDots"
                        tag="button"
                        neutral
                        class="c-fileListEntry__action"
                        data-qa="mobile attachment action dropdown toggle"
                        :aria-label="`Optionen für ${fileName} als Dropdown öffnen`"
                        type="button"
                        @click="toggleDropdown"
                    >
                        <AppIcon
                            class="rotate-90"
                            name="dots"
                            size="l"
                        />
                    </AppLink>
                    <ul
                        v-if="dropdownActive"
                        ref="optionsDropdown"
                        class="c-fileListEntry__dropdown"
                    >
                        <AppLink
                            neutral
                            class="c-fileListEntry__dropdownItem k-c-dropdown__listItem p-m hover:no-underline"
                            target="_blank"
                            rel="noopener noreferrer"
                            :aria-label="`${fileName} in neuem Tab betrachten`"
                            data-qa="preview link"
                            :href="previewLink"
                            @click="trackPreview"
                        >
                            Vorschau
                        </AppLink>
                        <AppLink
                            neutral
                            tag="button"
                            class="c-fileListEntry__dropdownItem k-c-dropdown__listItem p-m hover:no-underline
                                   text-left"
                            :aria-label="`${fileName} aus Anhängen entfernen`"
                            type="button"
                            @click="removeUploadedCv"
                        >
                            Entfernen
                        </AppLink>
                    </ul>
                </template>
            </template>
            <template v-else>
                <div class="c-fileListEntry__actions">
                    <template v-if="!showUploadProgress">
                        <AppLink
                            neutral
                            class="c-fileListEntry__action"
                            :aria-label="`${fileName} in neuem Tab betrachten`"
                            :href="previewLink"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-qa="preview link"
                            @click="trackPreview"
                        >
                            <AppIcon
                                name="preview"
                                size="xl"
                            />
                        </AppLink>
                        <AppLink
                            tag="button"
                            neutral
                            class="c-fileListEntry__action"
                            :aria-label="`${fileName} aus Anhängen entfernen`"
                            type="button"
                            @click="removeUploadedCv"
                        >
                            <AppIcon
                                name="dustbin"
                                size="xl"
                            />
                        </AppLink>
                    </template>
                    <template v-else-if="showUploadProgress">
                        <AppLink
                            tag="button"
                            neutral
                            class="c-fileListEntry__action"
                            :aria-label="`Upload von ${fileName} abbrechen`"
                            type="button"
                            @click="abortUpload"
                        >
                            <AppIcon
                                name="cross"
                                size="xl"
                            />
                        </AppLink>
                    </template>
                </div>
            </template>
        </template>
    </IconListItem>
</template>

<style lang="scss">
@use '~@karriereat/global-styles/scss/settings/spacing';
@use '~@karriereat/global-styles/scss/components/dropdown';
@use '~@karriereat/global-styles/scss/objects/chain';
@use '~@karriereat/global-styles/scss/components/link/settings' as link;
@use '~@karriereat/global-styles/scss/components/list/index' as list;
@use '../../../utils/reset';

$progress-indicator-scaling: 1.4;
$dropdown-width: 8rem;
$pdf-preview-offset: translate(spacing.$k-spacing--xs, spacing.$k-spacing--xs);
$pdf-background-color: var(--k-white);

.c-fileListEntry {
    &__progressIndicator {
        scale: $progress-indicator-scaling;
    }

    &__actions {
        position: relative;
        display: flex;
        gap: spacing.$k-spacing--xl;
    }

    &__action {
        display: flex;
        align-items: center;
        @include reset.buttonReset();
    }

    &__dropdown {
        @extend .k-c-dropdown__list;
        @extend .k-c-dropdown__list--open;
        right: 0;
        z-index: 1;
        width: $dropdown-width;
        border-top: 1px solid var(--k-gray-300);
    }

    &__dropdownItem {
        @include reset.buttonReset();
        display: block;
        width: 100%;
    }

    &__katLogo {
        position: absolute;
        right: spacing.$k-spacing--xs;
        bottom: spacing.$k-spacing--xs;
        width: 1rem;
        height: 1rem;
    }

    &__pdfPreview {
        position: absolute;
        top: 0;
        left: 0;
        background-color: $pdf-background-color;
        transform: $pdf-preview-offset;
    }
}
</style>
